import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { ETypeOfNotification, Modal, setNotification } from 'medere-ui-framework';
import { HandoffReceiptDTO, HandoffReceiptDetailDTO, UserSessionDTO } from '../../../dto/locationcash/LocationCashDTO';
import { signIncomingHandoff } from '../../../services/LocationCashController';
import { parseApiError } from '../../../services/apiError';
import { formatDateTime, formatMoney, hasDiscrepancy, isValidDeclaredAmount } from '../utils';
import useCashDiscrepancyConfirmation from '../useCashDiscrepancyConfirmation';
import CashDiscrepancyConfirm from './CashDiscrepancyConfirm';

const DETAIL_OUTGOING = 1;

function getDetail(receipt: HandoffReceiptDTO, type: number): HandoffReceiptDetailDTO | undefined {
    return receipt.details.find(d => d.detailType === type);
}

// ── Modo Incoming: el usuario entrante firma el comprobante pendiente ──

interface IncomingHandoffModalProps {
    visible: boolean;
    onHide: () => void;
    receipt: HandoffReceiptDTO;
    onSuccess: (session: UserSessionDTO) => void;
}

export function IncomingHandoffModal({
    visible,
    onHide,
    receipt,
    onSuccess,
}: IncomingHandoffModalProps) {
    const dispatch = useDispatch();
    const [declaredAmount, setDeclaredAmount] = useState<number | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [incomingObservations, setIncomingObservations] = useState('');
    const [loading, setLoading] = useState(false);
    const discrepancy = useCashDiscrepancyConfirmation();

    const outgoing = getDetail(receipt, DETAIL_OUTGOING);
    const canSign = confirmed && isValidDeclaredAmount(declaredAmount);

    // El checkbox repite el monto declarado: al revisarlo hay que volver a confirmarlo
    const handleReview = () => {
        discrepancy.review();
        setConfirmed(false);
    };

    const handleSign = async (acceptDiscrepancy = false) => {
        if (!isValidDeclaredAmount(declaredAmount)) return;
        setLoading(true);
        try {
            // Asigna el puesto y firma el comprobante en una sola operación atómica
            const { session, differenceAmount } = await signIncomingHandoff({
                receiptId: receipt.receiptId,
                declaredAmount,
                incomingObservations: incomingObservations.trim() || undefined,
                acceptDiscrepancy,
            });
            setDeclaredAmount(null);
            setConfirmed(false);
            setIncomingObservations('');
            discrepancy.reset();
            dispatch(setNotification({
                title: 'Comprobante firmado',
                desc: hasDiscrepancy(differenceAmount)
                    ? 'Se confirmó la recepción del puesto y la caja con una diferencia pendiente de justificación'
                    : 'Se confirmó la recepción del puesto y la caja',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            onSuccess(session);
        } catch (err) {
            const apiError = parseApiError(err);
            if (discrepancy.handleRejection(apiError)) return;
            dispatch(setNotification({
                title: 'No se pudo firmar el comprobante',
                desc: apiError.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            visible={visible}
            setVisible={() => {}}
            header="Comprobante de traspaso pendiente"
            width="w-medium"
            closable={false}
        >
            {discrepancy.pending ? (
                <CashDiscrepancyConfirm
                    confirming={loading}
                    onReview={handleReview}
                    onConfirm={() => handleSign(true)}
                />
            ) : (
                <div className="flex flex-column gap-3">
                    <p className="m-0" style={{ color: '#5A6676', fontSize: '13px' }}>
                        El usuario anterior dejó un comprobante de traspaso pendiente de firma.
                        Contabilice el dinero en caja y declare el monto que encuentra.
                    </p>

                    <div className="handoff-receipt">
                        <h4 className="handoff-receipt-title">Comprobante de traspaso</h4>
                        <div className="grid">
                            <div className="col-6">
                                <span style={{ fontSize: '13px', color: '#5A6676' }}>Fecha y hora</span>
                                <p className="m-0 font-medium">{formatDateTime(receipt.handoffDatetime)}</p>
                            </div>
                            <div className="col-6">
                                <span style={{ fontSize: '13px', color: '#5A6676' }}>Puesto</span>
                                <p className="m-0 font-medium">{receipt.locationDescription}</p>
                            </div>
                            {outgoing && (
                                <div className="col-6">
                                    <span style={{ fontSize: '13px', color: '#5A6676' }}>Firmado por</span>
                                    <p className="m-0 font-medium">{outgoing.username}</p>
                                </div>
                            )}
                        </div>
                        {outgoing?.observations && (
                            <div className="mt-3">
                                <span style={{ fontSize: '13px', color: '#5A6676' }}>Observaciones del saliente</span>
                                <p className="m-0 font-medium">{outgoing.observations}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-column gap-1">
                        <label htmlFor="incoming-declared-amount" className="font-medium" style={{ fontSize: '13px' }}>Monto declarado *</label>
                        <InputNumber
                            inputId="incoming-declared-amount"
                            inputRef={discrepancy.declaredAmountRef}
                            value={declaredAmount}
                            onValueChange={e => setDeclaredAmount(e.value ?? null)}
                            mode="currency"
                            currency="ARS"
                            locale="es-AR"
                            min={0}
                            disabled={loading}
                            placeholder="Ingrese el monto contabilizado"
                        />
                    </div>

                    <div className="flex flex-column gap-2">
                        <label className="font-medium" style={{ fontSize: '13px' }}>Observaciones (opcional)</label>
                        <InputTextarea
                            value={incomingObservations}
                            onChange={(e) => setIncomingObservations(e.target.value)}
                            rows={2}
                            placeholder="Ingrese observaciones si corresponde..."
                            autoResize
                            disabled={loading}
                        />
                    </div>

                    <div className="flex align-items-center gap-2 mt-1">
                        <Checkbox
                            inputId="incoming-confirm"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(!!e.checked)}
                            disabled={loading || !isValidDeclaredAmount(declaredAmount)}
                        />
                        <label htmlFor="incoming-confirm" className="cursor-pointer" style={{ fontSize: '13px' }}>
                            Confirmo que recibo la caja con un monto de <strong>{formatMoney(declaredAmount)}</strong>
                        </label>
                    </div>

                    <div className="flex justify-content-end gap-2 mt-2">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="buttonMain3"
                            onClick={onHide}
                            disabled={loading}
                        />
                        <Button
                            label="Firmar"
                            icon="pi pi-check"
                            className="buttonMain"
                            onClick={() => handleSign()}
                            loading={loading}
                            disabled={!canSign}
                        />
                    </div>
                </div>
            )}
        </Modal>
    );
}
